import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { each, isUndefined } from 'lodash';
import { Grid } from '@material-ui/core';
import useForm from 'react-hook-form';
import { format } from 'date-fns';
import PropTypes, { oneOfType } from 'prop-types';
import ClaimStepNavigationWrapper from '../../../../components/ClaimFormComponents/ClaimStepNavigationWrapper';
import TextInputFieldWithValidation from '../../../../components/ValidationFormElements/TextInputFieldWithValidation';
import OtherCitizenForm
from '../../components/OtherCitizenForm';
import OtherPhysicalApplicantForm
from '../../components/OtherPhysicalApplicantForm';
import OtherLegalApplicantForm from '../../../../components/FormComponents/OtherLegalApplicantForm';
import ChangeApplicant from '../../../../components/ClaimFormComponents/ChangeApplicant';
import FormSection from '../../../../components/FormComponents/FormSection';
import useDeviceResize from '../../../../hooks/useDeviceResize';
import { DATE_FORMAT_SHORT, LANGUAGES } from '../../../../constants';
import { APPLICANT_TYPES } from '../../../../constants/claim';

import ApplicantInfoIcon from '../../../../img/formSectionIcons/applicantInfo.svg';
import ApplicantIcon from '../../../../img/formSectionIcons/applicant.svg';

const PersonalInformationStep = ({
  step,
  goNext,
  goPrevious,
  setStepNavigationBox,
  updateForm,
  validationSchema,
  claim,
}) => {
  const dispatch = useDispatch();
  const { personalData, language: formLanguage } = claim;
  const countries = useSelector((store) => store.countriesReducer.countries);
  const isDeviceLgSize = useDeviceResize('lg');

  const [givenDate, setGivenDate] = useState(personalData.passportGivenDate);
  const [applicantIs, setApplicantIs] = useState(APPLICANT_TYPES[0].value);
  const [validUntilDate, setValidUntilDate] = useState(personalData.passportValidUntilDate);
  const { isArmCitizen } = personalData;
  const {
    firstName: fNameFromBack,
    lastName: lNameFromBack,
    passportNum: passportNumFromBack,
  } = personalData.applicant;

  useEffect(() => {
    if (!isUndefined(personalData.applicantIs)) {
      setApplicantIs(personalData.applicantIs);
    }
  }, [personalData.applicantIs]);

  const {
    register,
    setValue,
    errors,
    triggerValidation,
    clearError,
    getValues,
    handleSubmit,
    unregister,
  } = useForm({
    validationSchema,
    mode: 'onBlur',
  });

  useEffect(() => {
    register({ name: 'isCitizen' });
    setValue('isCitizen', true);
  }, [setValue, register]);

  useEffect(() => {
    register({ name: 'applicantIs' });
  }, [register]);

  useEffect(() => {
    setValue('applicantIs', applicantIs);
  }, [applicantIs, setValue]);

  useEffect(() => {
    each(personalData, (value, key) => {
      setValue(key, value, !!value);
    });
  }, [personalData, setValue]);

  useEffect(() => {
    setValue('isCitizen', isArmCitizen);
  }, [isArmCitizen, setValue]);

  useEffect(() => {
    register({ name: 'formLanguage' });
  }, [register]);

  useEffect(() => {
    const { firstName, lastName } = getValues();
    setValue('firstName', firstName, !!firstName);
    setValue('lastName', lastName, !!lastName);
    setValue('formLanguage', formLanguage);
  }, [formLanguage, setValue, getValues]);

  useEffect(() => {
    if (formLanguage === LANGUAGES.HY) {
      if (fNameFromBack) {
        setValue('firstName', fNameFromBack);
      }
      if (lNameFromBack) {
        setValue('lastName', lNameFromBack);
      }
    }
  }, [formLanguage, fNameFromBack, lNameFromBack, setValue]);

  const updateValidUntilDate = useCallback(
    (newDate) => {
      setValidUntilDate(newDate ? format(newDate, DATE_FORMAT_SHORT) : null);
    },
    [],
  );

  const updateGivenDate = useCallback(
    (newDate) => {
      setGivenDate(newDate ? format(newDate, DATE_FORMAT_SHORT) : null);
    },
    [],
  );

  const handleChange = (target) => {
    setValue(target.name, target.value);
  };

  const onSubmit = useCallback(
    (data) => {
      dispatch(
        updateForm({
          name: 'personalData',
          data: {
            ...data,
            citizenship: personalData.citizenship,
          },
        }),
      );
      ['compensation', 'attachments'].forEach((section) => {
        dispatch(
          updateForm({
            name: section,
            data: {
              isOtherApplicant: ['other', 'otherLegal'].includes(data.applicantIs),
            },
          }),
        );
      });
      clearError();
      goNext(step);
    },
    [clearError, dispatch, goNext, personalData.citizenship, step, updateForm],
  );

  return (
    <div>
      <ClaimStepNavigationWrapper
        clearError={clearError}
        errors={errors}
        isNextDisabled={false}
        handleSubmit={handleSubmit}
        setStepNavigationBox={setStepNavigationBox}
        isDeviceLgSize={isDeviceLgSize}
        onSubmit={onSubmit}
        back={goPrevious}
        dependencies={[JSON.stringify(errors), isDeviceLgSize, false]}
      />
      <FormSection title="claim.personalDataTitle" icon={ApplicantInfoIcon}>
        <Grid container spacing={4}>
          <Grid item md={4} xs={12}>
            <TextInputFieldWithValidation
              formProps={{ errors, clearError }}
              name="firstName"
              disabled={!!fNameFromBack && formLanguage === LANGUAGES.HY}
              autoComplete="off"
              register={register}
            />
          </Grid>
          <Grid item md={4} xs={12}>
            <TextInputFieldWithValidation
              formProps={{ errors, clearError }}
              name="lastName"
              disabled={!!lNameFromBack && formLanguage === LANGUAGES.HY}
              autoComplete="off"
              register={register}
            />
          </Grid>
          <Grid item md={4} xs={12}>
            <TextInputFieldWithValidation
              formProps={{ errors, clearError }}
              name="passportNum"
              disabled={!!passportNumFromBack}
              autoComplete="off"
              register={register}
            />
          </Grid>
        </Grid>
      </FormSection>
      <FormSection title="personalClaim.personalData.applicantIs" icon={ApplicantIcon}>
        <ChangeApplicant
          value={applicantIs}
          onChange={(value) => {
            setApplicantIs(value);
          }}
        />
        {!isArmCitizen && (
        <OtherCitizenForm
          givenDate={givenDate}
          updateGivenDate={updateGivenDate}
          handleChange={handleChange}
          clearError={clearError}
          validUntilDate={validUntilDate}
          errors={errors}
          updateValidUntilDate={updateValidUntilDate}
          register={register}
          setValue={setValue}
          unregister={unregister}
        />
        )}

        {applicantIs === APPLICANT_TYPES[1].value && (
          <div>
            <OtherPhysicalApplicantForm
              initialData={{ ...personalData, applicantIs }}
              formProps={{
                errors,
                clearError,
                getValues,
                setValue,
                register,
                unregister,
                triggerValidation,
              }}
              countries={countries}
            />
          </div>
        )}
        {applicantIs === APPLICANT_TYPES[2].value && (
          <div>
            <OtherLegalApplicantForm
              initialData={{ ...personalData, applicantIs }}
              formProps={{
                register,
                setValue,
                errors,
                clearError,
                triggerValidation,
              }}
              valueNames={{
                hvhh: 'applicantOrganizationHvhh',
                organizationName: `applicantOrganizationName${formLanguage.toUpperCase()}`,
                email: 'applicantOrganizationEmail',
                phone: 'applicantOrganizationPhone',
              }}
            />
          </div>
        )}
      </FormSection>
    </div>
  );
};

PersonalInformationStep.propTypes = {
  goPrevious: PropTypes.func.isRequired,
  goNext: PropTypes.func.isRequired,
  step: PropTypes.shape({
    claimForm: PropTypes.string.isRequired,
    n: PropTypes.number.isRequired,
  }).isRequired,
  setStepNavigationBox: PropTypes.func.isRequired,
  updateForm: PropTypes.func.isRequired,
  validationSchema: PropTypes.shape({}).isRequired,
  claim: PropTypes.shape({
    personalData: PropTypes.shape({
      isArmCitizen: PropTypes.bool.isRequired,
      citizenship: PropTypes.shape({}).isRequired,
      applicant: PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        passportNum: PropTypes.string,
      }).isRequired,
      passportGivenDate: oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
      passportValidUntilDate: oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
      applicantIs: PropTypes.string,
    }).isRequired,
    language: PropTypes.string.isRequired,
  }).isRequired,
};

export default PersonalInformationStep;
